import { useState } from 'react';
import { AudioLines, Loader2, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { SpeechSettings } from '../components/speech/SpeechSettings';
import { SpeechPlayer } from '../components/speech/SpeechPlayer';
import { useManagedSpeech } from '../hooks/useManagedSpeech';
import { readSpeechSettings, saveSpeechSettings } from '../lib/managedSpeech';
import '../styles/managed-speech.css';

export default function SpeechStudio() {
  const [text, setText] = useState('I am Aria, the Heart of the Federation. The forge is lit. Let your words become a voice.');
  const [settings, setSettings] = useState(readSpeechSettings);
  const speech = useManagedSpeech();
  const busy = speech.status === 'generating';
  const changeSettings = (next) => { setSettings(next); saveSpeechSettings(next); };

  return (
    <main className="speech-studio managed-speech" data-testid="speech-studio">
      <div className="speech-inner">
        <header className="speech-heading">
          <img src="/assets/aria-face.jpeg" alt="Aria, the Heart" data-testid="speech-aria-portrait" />
          <div>
            <p className="speech-eyebrow" data-testid="speech-studio-provider">NEXTXUS · EMERGENT-MANAGED OPENAI</p>
            <h1 data-testid="speech-studio-title" className="text-4xl sm:text-5xl lg:text-6xl">Speech Studio</h1>
            <p data-testid="speech-disclosure">AI-generated voice · Uses Universal Key credits</p>
          </div>
        </header>
        <form data-testid="speech-form" onSubmit={(e) => { e.preventDefault(); speech.generate(text, settings); }}>
          <SpeechSettings settings={settings} onChange={changeSettings} disabled={busy} testId="speech" />
          <div className="speech-text-heading">
            <label htmlFor="speech-text">Your words</label>
            <output data-testid="speech-character-count">{text.length.toLocaleString()} / 4,096</output>
          </div>
          <Textarea id="speech-text" data-testid="speech-text" value={text} maxLength={4096} disabled={busy}
            placeholder="Give your words a voice…" onChange={(e) => setText(e.target.value)} />
          <div className="speech-actions">
            <Button type="submit" data-testid="speech-generate" disabled={busy || !text.trim()}>
              {busy ? <Loader2 className="animate-spin" /> : <AudioLines />} {busy ? 'Generating…' : 'Generate speech'}
            </Button>
            {(busy || speech.result) && <Button type="button" variant="outline" data-testid="speech-stop" onClick={speech.stop}>
              <Square /> {busy ? 'Cancel' : 'Stop'}
            </Button>}
            <span role="status" aria-live="polite" data-testid="speech-status">
              {speech.status === 'ready' ? 'Recording ready' : speech.status === 'playing' ? 'Playing' : speech.status === 'paused' ? 'Paused' : ''}
            </span>
          </div>
        </form>
        {speech.error && <p role="alert" className="speech-error" data-testid="speech-error">{speech.error}</p>}
        {speech.result && <section aria-label="Generated recording" className="speech-result" data-testid="speech-result">
          <h2 className="text-base md:text-lg" data-testid="speech-recording-title">Your recording</h2>
          <p data-testid="speech-recording-details">{speech.result.model} · {speech.result.voice} · {speech.result.speed}× · MP3</p>
          <SpeechPlayer speech={speech} testId="speech" />
          <p className="speech-retention" data-testid="speech-retention">Download available for 24 hours.</p>
        </section>}
      </div>
    </main>
  );
}